import { LoadingOutlined } from '@ant-design/icons'
import { formatCrmDateTimeMsk } from '@pages/Admin/CRM/utils/formatData'
import cn from 'classnames'
import { FC } from 'react'
import type { CrmIntegrationMessageDisplay } from '../../types/crm-integration-message.types'
import { CRM_INTEGRATION_SEND_FAILURE_MESSAGE } from '../../lib/crmIntegrationApiErrors'
import { partitionCrmIntegrationAttachments } from '../../lib/crmIntegrationAttachment'
import { renderCrmIntegrationMessageText } from '../../lib/renderCrmIntegrationMessageText'
import CrmIntegrationIncomingAttachments from './CrmIntegrationIncomingAttachments'
import classes from '../../styles/crmIntegrationChat.module.css'

type CrmIntegrationTextChatMessageListItemProps = {
  message: CrmIntegrationMessageDisplay
  onRetrySend: (clientId: string) => void
  onVideoPlaybackError?: () => void
  onVoicePlaybackError?: () => void
}

const CrmIntegrationTextChatMessageListItem: FC<
  CrmIntegrationTextChatMessageListItemProps
> = ({ message, onRetrySend, onVideoPlaybackError, onVoicePlaybackError }) => {
  const isOutgoing = message.direction === 'outgoing'
  const isSendingMessage = message.sendState === 'sending'
  const isFailedMessage = message.sendState === 'error'
  const messageText = message.text?.trim() ?? ''
  const failedSendClientId =
    isFailedMessage && isOutgoing ? message.clientId ?? null : null

  const { images, files, videos, voices, audios, unsupported } =
    partitionCrmIntegrationAttachments(message.attachments)
  const hasRenderableAttachments =
    images.length > 0 ||
    files.length > 0 ||
    videos.length > 0 ||
    voices.length > 0 ||
    audios.length > 0 ||
    unsupported.length > 0
  const hasMainBubbleContent = Boolean(messageText) || hasRenderableAttachments

  const bubbleClassName = isOutgoing
    ? classes.bubbleOutgoing
    : classes.bubbleIncoming

  const senderName = isOutgoing ? message.senderName?.trim() || null : null

  const bubbleMeta = (
    <span
      className={cn(classes.bubbleMeta, {
        [classes.bubbleMeta_withAuthor]: Boolean(senderName),
      })}
    >
      {senderName ? (
        <span className={classes.bubbleAuthor} title={`Автор: ${senderName}`}>
          Автор: {senderName}
        </span>
      ) : null}
      {isSendingMessage ? (
        <LoadingOutlined
          className={classes.bubbleSpinner}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      ) : null}
      <span className={classes.bubbleTime}>
        {formatCrmDateTimeMsk(message.createdAt)}
      </span>
    </span>
  )

  const showFailedActions = Boolean(failedSendClientId)

  return (
    <li
      className={
        isOutgoing ? classes.messageRowOutgoing : classes.messageRowIncoming
      }
    >
      <div className={classes.messageColumn}>
        {hasMainBubbleContent ? (
          <div
            className={bubbleClassName}
            data-sending={isSendingMessage ? 'true' : undefined}
            data-failed={isFailedMessage ? 'true' : undefined}
          >
            {messageText ? (
              <p
                className={cn(classes.bubbleText, {
                  [classes.bubbleTextAboveMedia]: hasRenderableAttachments,
                })}
              >
                {renderCrmIntegrationMessageText(messageText)}
              </p>
            ) : null}
            {hasRenderableAttachments ? (
              <CrmIntegrationIncomingAttachments
                attachments={message.attachments}
                onVideoPlaybackError={onVideoPlaybackError}
                onVoicePlaybackError={onVoicePlaybackError}
              />
            ) : null}
            {bubbleMeta}
          </div>
        ) : null}
        {showFailedActions ? (
          <div className={classes.sendFailed}>
            <span className={classes.sendFailedText}>
              {message.sendError ?? CRM_INTEGRATION_SEND_FAILURE_MESSAGE}
            </span>
            <div className={classes.failedButtons}>
              <button
                type="button"
                className={classes.failedButton}
                disabled={isSendingMessage}
                onClick={() => {
                  if (failedSendClientId) {
                    onRetrySend(failedSendClientId)
                  }
                }}
              >
                Отправить снова
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </li>
  )
}

export default CrmIntegrationTextChatMessageListItem
